import React, { useEffect, useState, lazy, Suspense } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import HBN from "../../assets/HBN.svg";
import { useSelector, useDispatch } from "react-redux";
import defaultProfilePic from "@/assets/default-icon.jpg";
import { clearAuthInfo, setCurrentLocation } from "@/redux/auth/auth.slice";
import { ToastContainer } from "react-toastify";
import { useNavigate, Link, useLocation } from "react-router-dom";
import TextsmsRoundedIcon from "@mui/icons-material/TextsmsRounded";
import SearchVendorIcon from "@/components/Icons/SearchVendor";
import { Home, Settings, MarkUnreadChatAlt } from "@mui/icons-material";
import { List, ListItem, ListItemPrefix } from "@material-tailwind/react";
import "react-toastify/dist/ReactToastify.css";
import "./Header.css";
import LoanApplicationIcon from "@/components/Icons/LoanApplicationIcon";
import LoginPopup from "@/common/LoginPopup";

const LogoutModal = lazy(() => import("@/shared/LogoutModal/LogoutModal"));

const PurpleTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: "#6366f1",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "10px",
    borderRadius: "6px",
  },
}));

const Header = ({ toggleSidebar }) => {
  const user = useSelector((state) => state.auth.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const isActive = (path) => location.pathname === path;
  const isAuthenticated = Boolean(localStorage.getItem("access_token"));
  useEffect(() => {
    if (user?.role === "customer" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          dispatch(
            setCurrentLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            })
          );
        },
        (err) => console.error(err)
      );
    }
  }, [dispatch, user?.role]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  const handleInboxClick = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setRedirectUrl("/customer/auth/login");
      setShowPopup(true);
    }
  };
  const handleLogout = () => {
    setLogoutOpen(false);
    dispatch(clearAuthInfo());
  };
  const handleNavigation = (section) => {
    if (location.pathname === "/privacy-policy" || location.pathname === "/terms" || location.pathname === "/faqs" || location.pathname === "/about-us") {
      navigate("/");
      // setTimeout(() => {
      scrollToSection(section);
      // }, 100); // delay to ensure home is loaded
    } else {
      scrollToSection(section);
    }
  };
  const menuItems = {
    vendor: [
      { path: "/vendor/dashboard", label: "Home", icon: <Home /> },
      { path: "/vendor/inbox", label: "Inbox", icon: <TextsmsRoundedIcon /> },
      { path: "/vendor/loan-applications", label: "Lead Applications", icon: <LoanApplicationIcon /> },
      { path: "/vendor/settings", label: "Settings", icon: <Settings /> },
    ],
  };

  const handleProfileClick = () => {
    navigate(user?.role === "customer" ? "/customer/settings" : "/vendor/settings");
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={5}
        sx={{
          backgroundColor: "#fff",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
          py: 0.5,
          zIndex: 1300,
        }}
      >
        <ToastContainer />
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 3 },
          }}
        >

          {/* Left: Logo + Conditional Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Link to={localStorage.getItem("role") === "vendor" ? "/vendor/dashboard" : "/"}>
              <Box
                component="img"
                src={HBN}
                alt="HBN Logo"
                sx={{ height: 42, width: "auto", cursor: "pointer" }}
              />
            </Link>

            {/* Show home page buttons if on "/" and not vendor/customer */}
            {(location.pathname === "/" || location.pathname === "/privacy-policy" || location.pathname === "/terms" || location.pathname === "/faqs" || location.pathname === "/about-us") && (
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  gap: 3,
                  alignItems: "center",
                }}
              >
                <button
                  onClick={() => handleNavigation("home")}
                  className="text-gray-700 hover:text-purple transition-colors duration-200 font-medium"
                >
                  Home
                </button>
                <button
                  onClick={() => navigate("/faqs")}
                  className="text-gray-700 hover:text-purple transition-colors duration-200 font-medium"
                >
                  First Home Buyer Guides
                </button>
                <button
                  onClick={() => handleNavigation("categories")}
                  className="text-gray-700 hover:text-purple transition-colors duration-200 font-medium"
                >
                  Explore Services
                </button>
              </Box>
            )}

            {/* Desktop: Show buttons ONLY if not on home and user is customer or unauthenticated */}
            {(location.pathname !== "/" && location.pathname != "/privacy-policy" && location.pathname != "/terms" && location.pathname != "/faqs" && location.pathname != "/about-us") && (user?.role === "customer" || !isAuthenticated) && (
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Link
                  to="/customer/search-brokers"
                  className={`header-button ${isActive("/customer/search-brokers") ? "active" : ""}`}
                >
                  <SearchVendorIcon className={`icon ${isActive("/customer/search-brokers") ? "active-icon" : "default-icon"}`} />
                  Find Professionals
                </Link>

                <Link
                  to={isAuthenticated ? "/customer/inbox" : "#"}
                  onClick={handleInboxClick}
                  className={`header-button ${isActive("/customer/inbox") ? "active" : ""}`}
                >
                  <TextsmsRoundedIcon className={`icon ${isActive("/customer/inbox") ? "active-icon" : "default-icon"}`} />
                  Inbox
                </Link>
              </Box>
            )}
          </Box>

          {/* Right: Hamburger for mobile, profile, logout */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

            {/* Home Page */}
            {(location.pathname === "/" || location.pathname === "/privacy-policy" || location.pathname === "/terms" || location.pathname === "/faqs" || location.pathname === "/about-us") && (
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <IconButton onClick={handleMenuOpen}>
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => { handleNavigation("home"); handleMenuClose(); }}>
                    Home
                  </MenuItem>
                  <MenuItem onClick={() => { navigate("/faqs"); handleMenuClose(); }}>
                    First Home Buyer Guides
                  </MenuItem>
                  <MenuItem onClick={() => { handleNavigation("categories"); handleMenuClose(); }}>
                    Explore Services
                  </MenuItem>
                </Menu>
              </Box>
            )}

            {/* Vendor: Hamburger for mobile */}
            {(location.pathname !== "/" && location.pathname != "/privacy-policy" && location.pathname != "/terms" && location.pathname != "/faqs" && location.pathname != "/about-us") && user?.role === "vendor" && (
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <IconButton onClick={handleMenuOpen}>
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  {menuItems.vendor.map((item) => (
                    <ListItem
                      key={item.path}
                      onClick={() => {
                        navigate(item.path);
                        handleMenuClose(); // ✅ Close sidebar after navigation
                      }}
                      className={`menu-item ${isActive(item.path) ? "active" : ""}`}
                    >
                      <ListItemPrefix>
                        {React.cloneElement(item.icon, {
                          className: `icon ${isActive(item.path) ? "active-icon" : "default-icon"}`,
                        })}
                      </ListItemPrefix>
                      <span>{item.label}</span>
                    </ListItem>
                  ))}
                </Menu>
              </Box>
            )}

            {/* Mobile dropdown menu */}
            {(location.pathname !== "/" && location.pathname != "/privacy-policy" && location.pathname != "/terms" && location.pathname != "/faqs" && location.pathname != "/about-us") && (user?.role === "customer" || !isAuthenticated) && (
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <IconButton onClick={handleMenuOpen}>
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <Link to="/customer/search-brokers" className="menu-link" onClick={handleMenuClose}>
                    <SearchVendorIcon className={`icon ${isActive("/customer/search-brokers") ? "active-icon" : "default-icon"}`} /> Find Professionals
                  </Link>
                  <Link to={isAuthenticated ? "/customer/inbox" : "/customer/auth/login"} className="menu-link" onClick={handleMenuClose}>
                    <TextsmsRoundedIcon className={`icon ${isActive("/customer/inbox") ? "active-icon" : "default-icon"}`} /> Inbox
                  </Link>
                </Menu>
              </Box>
            )}

            {/* Profile & Logout */}
            {isAuthenticated ? (
              <>
                <Button
                  disableRipple
                  onClick={handleProfileClick}
                  sx={{
                    border: "1px solid #E0E0E0",
                    display: "flex",
                    alignItems: "center",
                    textTransform: "none",
                    gap: 1,
                    bgcolor: "transparent",
                    p: 1,
                    "&:hover": { bgcolor: "transparent" },
                  }}
                >
                  <Avatar
                    src={user?.profile_picture?.file_url || defaultProfilePic}
                    alt="User"
                    sx={{ width: 38, height: 38 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#333",
                      fontWeight: 500,
                      maxWidth: 140,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(() => {
                      const name =
                        user?.full_name?.trim() ||
                        `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
                      return name.length > 15 ? `${name.slice(0, 15)}…` : name;
                    })()}
                  </Typography>
                </Button>

                <PurpleTooltip title="Logout" arrow>
                  <IconButton onClick={() => setLogoutOpen(true)}>
                    <LogoutIcon />
                  </IconButton>
                </PurpleTooltip>
              </>
            ) : (
              <>
                {location.pathname == "/" ? (<>
                  {/* <Button
                    onClick={() => navigate("/vendor/auth/login")}
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      color: "#6A00F4",
                      borderColor: "#6A00F4",
                      px: 2.5,
                      py: 0.8,
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "#6A00F4",
                        color: "#fff",
                        borderColor: "#6A00F4",
                      },
                    }}
                  >
                    Signup as Expert
                  </Button> */}

                  <Button
                    onClick={() => navigate("/customer/auth/login")}
                    variant="contained"
                    size="small"
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#6A00F4",
                      borderRadius: "8px",
                      px: 2.5,
                      py: 0.8,
                      fontWeight: 500,
                      ml: 1,
                      "&:hover": {
                        backgroundColor: "#5800CC",
                      },
                    }}
                  >
                    Signup / Login
                  </Button>
                </>) : (<>
                  {/* <Button
                    onClick={() => navigate("/vendor/auth/login")}
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: "none",
                      borderRadius: "8px",
                      color: "#6A00F4",
                      borderColor: "#6A00F4",
                      px: 2.5,
                      py: 0.8,
                      fontWeight: 500,
                      "&:hover": {
                        backgroundColor: "#6A00F4",
                        color: "#fff",
                        borderColor: "#6A00F4",
                      },
                    }}
                  >
                    Signup as Expert
                  </Button> */}

                  <Button
                    onClick={() => navigate("/customer/auth/login")}
                    variant="contained"
                    size="small"
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#6A00F4",
                      borderRadius: "8px",
                      px: 2.5,
                      py: 0.8,
                      fontWeight: 500,
                      ml: 1,
                      "&:hover": {
                        backgroundColor: "#5800CC",
                      },
                    }}
                  >
                    Signup / Login
                  </Button>
                </>)}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Logout Modal */}
      {logoutOpen && (
        <Suspense
          fallback={
            <Box textAlign="center" p={2}>
              Loading…
            </Box>
          }
        >
          <LogoutModal
            open={logoutOpen}
            onClose={() => setLogoutOpen(false)}
            onLogout={handleLogout}
          />
        </Suspense>
      )}
      {showPopup && (
        <LoginPopup
          onClose={() => setShowPopup(false)}
          redirectUrl={redirectUrl}
        />
      )}
    </>
  );
};

export default Header;
